import { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Tooltip,
  Card,
  Input,
  Select,
  List,
  Row,
  Col,
  message,
  Modal,
  Table,
  Upload,
  Image,
  Pagination,
  Popover,
  Checkbox,
  Tag,
} from "antd";
import {
  PaperClipOutlined,
  UploadOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import { PlusIcon, Edit2, Trash2, Search } from "lucide-react";
import {
  listFaqsApi,
  createFaqApi,
  updateFaqApi,
  deleteFaqApi,
  uploadFaqAttachmentApi,
  getFaqTagsApi,
} from "../../services/faq.service";
import { useDispatch } from "react-redux";
import FaqFileManagerDialog from "./FaqFileManagerDialog";
import FaqTagsEditor from "./FaqTagsEditor";
import StrictBulletListEditor from "./StrictBulletListEditor";

const { Title, Text } = Typography;
const IMAGE_EXTENSION_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|avif)$/i;
const INLINE_NEW_FAQ_ID = "__inline_new_faq__";
const INLINE_DRAFT_ANIMATION_MS = 220;
const normalizeQuestionsForListEditor = (questions = []) => {
  const normalized = (Array.isArray(questions) ? questions : []).flatMap((question) => {
    if (typeof question !== "string") return [];
    return question.split(/\r?\n/);
  });
  return normalized.length ? normalized : [""];
};

const DebouncedTextArea = ({ value, onChange, delay = 400, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  const isTyping = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isTyping.current) {
      setLocalValue(value);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    isTyping.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      isTyping.current = false;
      onChange(newValue);
    }, delay);
  };

  return <Input.TextArea {...props} value={localValue} onChange={handleChange} />;
};


const FaqComponent = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagsFilter, setTagsFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [faqIdsToDelete, setFaqIdsToDelete] = useState([]);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [editingFaqData, setEditingFaqData] = useState(null);
  const [highlightedFaqId, setHighlightedFaqId] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedFaqIds, setSelectedFaqIds] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [tagUpdatingId, setTagUpdatingId] = useState(null);
  const [openStatusPopoverId, setOpenStatusPopoverId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [totalFaqs, setTotalFaqs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hoveredAttachmentKey, setHoveredAttachmentKey] = useState(null);
  const [failedImageKeys, setFailedImageKeys] = useState(() => new Set());
  const [manageFilesOpen, setManageFilesOpen] = useState(false);
  const [faqTagSuggestions, setFaqTagSuggestions] = useState([]);
  const [loadingFaqTagSuggestions, setLoadingFaqTagSuggestions] = useState(false);
  const [inlineDraftVisible, setInlineDraftVisible] = useState(false);
  const [inlineDraftMounted, setInlineDraftMounted] = useState(false);
  const pendingCreatedFaqRef = useRef(null);
  const skipNextListLoadingRef = useRef(false);
  const highlightTimeoutRef = useRef(null);
  const inlineDraftTimeoutRef = useRef(null);
  const faqSectionRef = useRef(null);

  const mapFaqItemToViewModel = (item) => {
    const normalizeFaqTags = (rawTags) => {
      if (!rawTags) return [];
      if (Array.isArray(rawTags)) {
        return Array.from(
          new Set(
            rawTags
              .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
              .filter(Boolean)
          )
        );
      }
      if (typeof rawTags === "string") {
        return Array.from(
          new Set(
            rawTags
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean)
          )
        );
      }
      return [];
    };

    const normalizeAttachmentValue = (attachment) => {
      if (!attachment) return null;
      if (typeof attachment === "string") return attachment.trim() || null;
      if (typeof attachment === "object") {
        const candidate =
          attachment.urls ||
          attachment.url ||
          attachment.attachments ||
          attachment.attachment ||
          attachment.file_name ||
          attachment.filename ||
          attachment.name;
        return typeof candidate === "string" ? candidate.trim() || null : null;
      }
      return null;
    };

    const normalizeAttachments = (rawAttachments) => {
      if (!rawAttachments) return [];
      const list = Array.isArray(rawAttachments)
        ? rawAttachments
        : [rawAttachments];
      return list
        .map((attachment) => normalizeAttachmentValue(attachment))
        .filter(Boolean);
    };

    const normalizedQuestions = Array.isArray(item?.questions)
      ? item.questions
        .map((q) => (typeof q === "string" ? q : q?.question))
        .filter(Boolean)
      : [];
    const normalizedAttachments = [
      ...normalizeAttachments(item?.answer?.attachments),
      ...normalizeAttachments(item?.answer?.attachment),
    ];

    return {
      id: item?.id,
      questions: normalizedQuestions,
      answer:
        typeof item?.answer === "string" ? item.answer : item?.answer?.text || "",
      attachments: normalizedAttachments,
      status: item?.is_verified ? "verified" : "unverified",
      sociair_id: item?.sociair_id || "",
      tags: normalizeFaqTags(item?.tags),
      created_at: item?.created_at,
      created_by: item?.created_by,
      updated_at: item?.updated_at,
      updated_by: item?.updated_by,
      verified_by: item?.verified_by,
      source: item?.source || "",
    };
  };

  const getUploadAttachmentNames = (response) => {
    const container = response?.data ?? response;
    const arrayContainer = Array.isArray(container) ? container : [container];

    const names = arrayContainer
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.name || item?.file_name || item?.filename || "";
      })
      .filter((value) => typeof value === "string" && value.trim())
      .map((value) => value.trim());

    return Array.from(new Set(names));
  };

  const uploadAttachmentsAndGetNames = (files, sourceName = "faq") =>
    new Promise((resolve, reject) => {
      if (!files?.length) {
        resolve([]);
        return;
      }

      const normalizedSource =
        typeof sourceName === "string" && sourceName.trim()
          ? sourceName.trim()
          : "faq";

      dispatch(
        uploadFaqAttachmentApi({
          files,
          source: normalizedSource,
          finalCallback: () => { },
          successCallback: (response) => {
            const uploadedNames = getUploadAttachmentNames(response);
            if (!uploadedNames.length) {
              reject(new Error("Attachment upload succeeded but no file names were returned."));
              return;
            }
            resolve(uploadedNames);
          },
          failureCallback: (error) => {
            reject(error);
          },
        })
      );
    });

  const getAttachmentLabel = (attachmentUrl) => {
    if (!attachmentUrl) return "";
    try {
      const url = new URL(attachmentUrl);
      const pathname = url.pathname || "";
      const fileName = pathname.split("/").filter(Boolean).pop();
      return fileName || attachmentUrl;
    } catch (e) {
      const fileName = attachmentUrl.split("/").filter(Boolean).pop();
      return fileName || attachmentUrl;
    }
  };

  const isUrl = (value) => {
    if (!value || typeof value !== "string") return false;
    try {
      const parsedUrl = new URL(value);
      return Boolean(parsedUrl.protocol && parsedUrl.host);
    } catch {
      return false;
    }
  };

  const isImageFile = (file) => {
    if (!file) return false;
    const mimeType = typeof file.type === "string" ? file.type : "";
    if (mimeType.toLowerCase().startsWith("image/")) return true;
    return IMAGE_EXTENSION_REGEX.test(file?.name || "");
  };

  const validateImageBeforeUpload = (file) => {
    if (isImageFile(file)) return false;
    message.error(`${file?.name || "Selected file"} is not an image.`);
    return Upload.LIST_IGNORE;
  };

  const isImageAttachment = (attachment) => {
    if (!attachment || typeof attachment !== "string") return false;
    if (attachment.toLowerCase().startsWith("data:image/")) return true;
    try {
      const parsedUrl = new URL(attachment);
      return IMAGE_EXTENSION_REGEX.test(parsedUrl.pathname || "");
    } catch {
      return IMAGE_EXTENSION_REGEX.test(attachment.split("?")[0] || "");
    }
  };

  const getAttachmentPreviewSrc = (attachment) => {
    if (!attachment || typeof attachment !== "string") return null;
    if (attachment.toLowerCase().startsWith("data:image/")) return attachment;
    if (isUrl(attachment) || attachment.startsWith("/")) return attachment;
    return null;
  };

  const markImageFailed = (imageKey) => {
    if (!imageKey) return;
    setFailedImageKeys((prev) => {
      if (prev.has(imageKey)) return prev;
      const next = new Set(prev);
      next.add(imageKey);
      return next;
    });
  };

  const clearImageFailed = (imageKey) => {
    if (!imageKey) return;
    setFailedImageKeys((prev) => {
      if (!prev.has(imageKey)) return prev;
      const next = new Set(prev);
      next.delete(imageKey);
      return next;
    });
  };

  const getFaqImagePreviewStyle = (imageKey) =>
    failedImageKeys.has(imageKey)
      ? faqImagePreviewErrorStyle
      : faqImagePreviewStyle;

  const renderAttachment = (attachment, imageKey) => {
    const previewSrc = isImageAttachment(attachment)
      ? getAttachmentPreviewSrc(attachment)
      : null;
    const label = getAttachmentLabel(attachment);

    if (previewSrc) {
      return (
        <Image
          src={previewSrc}
          alt={"FAQ attachment"}
          style={getFaqImagePreviewStyle(imageKey)}
          onError={() => markImageFailed(imageKey)}
          onLoad={() => clearImageFailed(imageKey)}
          preview
        />
      );
    }

    if (isUrl(attachment)) {
      return (
        <a href={attachment} target="_blank" rel="noreferrer">
          <PaperClipOutlined style={{ marginRight: "6px" }} />
          {label}
        </a>
      );
    }

    return (
      <Text>
        <PaperClipOutlined style={{ marginRight: "6px" }} />
        {attachment}
      </Text>
    );
  };

  const getSelectedFilePreviewSrc = (file) => {
    if (!file) return null;
    if (typeof file.thumbUrl === "string" && file.thumbUrl) return file.thumbUrl;
    if (typeof file.url === "string" && file.url) return file.url;
    if (file.originFileObj) {
      try {
        return URL.createObjectURL(file.originFileObj);
      } catch {
        return null;
      }
    }
    return null;
  };

  const removeEditingAttachmentAt = (index) => {
    setEditingFaqData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        attachments: (Array.isArray(prev.attachments) ? prev.attachments : []).filter(
          (_, idx) => idx !== index
        ),
      };
    });
  };

  const removeEditingSelectedFileByUid = (uid) => {
    setEditingFaqData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        attachmentFileList: (Array.isArray(prev.attachmentFileList)
          ? prev.attachmentFileList
          : []
        ).filter((file) => file.uid !== uid),
      };
    });
  };

  const uploadCardStyle = {
    width: "140px",
    height: "80px",
    border: "1px dashed #d9d9d9",
    borderRadius: "8px",
    background: "#fafafa",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    padding: "12px",
    boxSizing: "border-box",
  };

  const faqImagePreviewStyle = {
    width: "auto",
    maxWidth: "100%",
    height: "80px",
    objectFit: "contain",
    borderRadius: "6px",
    border: "1px solid #f0f0f0",
    display: "block",
    boxSizing: "border-box",
  };

  const faqImagePreviewErrorStyle = {
    ...faqImagePreviewStyle,
    width: "80px",
    minWidth: "80px",
    maxWidth: "80px",
  };

  const getErrorMessage = (error) => {
    return error?.response?.data?.detail || error?.message || "An error occurred";
  };

  const normalizeFaqIds = (ids = []) =>
    Array.from(
      new Set(
        (ids || [])
          .filter(Boolean)
          .map((id) => (typeof id === "string" ? id.trim() : id))
          .filter((id) => id !== undefined && id !== null && id !== "")
      )
    );

  const setBulkDeleteIds = (ids = []) => {
    const normalized = normalizeFaqIds(ids);
    setFaqIdsToDelete(normalized);
  };

  const toggleFaqSelection = (faqId) => {
    setSelectedFaqIds((prev) => {
      if (prev.includes(faqId)) {
        return prev.filter((id) => id !== faqId);
      }
      return [...prev, faqId];
    });
  };

  const selectAllFaqs = (checked) => {
    setSelectedFaqIds((prev) => {
      if (checked) {
        return normalizeFaqIds(faqs.map((faq) => faq.id));
      }
      if (!prev.length) return prev;
      return [];
    });
  };

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      if (inlineDraftTimeoutRef.current) {
        clearTimeout(inlineDraftTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoadingFaqTagSuggestions(true);

    dispatch(
      getFaqTagsApi({
        finalCallback: () => {
          if (isMounted) {
            setLoadingFaqTagSuggestions(false);
          }
        },
        successCallback: (data) => {
          if (!isMounted) return;
          const normalizedSuggestions = Array.from(
            new Set(
              [
                ...(Array.isArray(data?.faq_tags) ? data.faq_tags : []),
                ...(Array.isArray(data?.products) ? data.products : []),
              ]
                .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
                .filter(Boolean)
            )
          );
          setFaqTagSuggestions(normalizedSuggestions);
        },
        failureCallback: (error) => {
          if (!isMounted) return;
          console.error("Failed to fetch FAQ tag suggestions:", error);
          setFaqTagSuggestions([]);
        },
      })
    );

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const fetchFaqs = (
    query = "",
    isVerified,
    sourceId,
    selectedTags = [],
    page = 1,
    limit = 10
  ) => {
    const shouldShowLoading = !skipNextListLoadingRef.current;
    skipNextListLoadingRef.current = false;
    if (shouldShowLoading) {
      setLoading(true);
    }
    const offset = (page - 1) * limit;
    const normalizedTags = Array.from(
      new Set(
        (Array.isArray(selectedTags) ? selectedTags : [])
          .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
          .filter(Boolean)
      )
    );
    const tagsParam = normalizedTags.length ? normalizedTags.join(",") : undefined;
    const onSuccess = (data) => {
      const mappedFaqs = (data?.items || []).map(mapFaqItemToViewModel);
      setFaqs(mappedFaqs);
      setTotalFaqs(data?.total || 0);
      setSelectedFaqIds((prevSelected) =>
        prevSelected.filter((faqId) => mappedFaqs.some((faq) => faq.id === faqId))
      );

      if (pendingCreatedFaqRef.current) {
        const { createdFaqId } = pendingCreatedFaqRef.current;
        pendingCreatedFaqRef.current = null;

        const targetFaqId = mappedFaqs.some((faq) => faq.id === createdFaqId)
          ? createdFaqId
          : mappedFaqs[0]?.id;

        const highlightCreatedFaq = () => {
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });

            if (!targetFaqId) return;

            setHighlightedFaqId(targetFaqId);
            if (highlightTimeoutRef.current) {
              clearTimeout(highlightTimeoutRef.current);
            }
            highlightTimeoutRef.current = setTimeout(() => {
              setHighlightedFaqId((prev) => (prev === targetFaqId ? null : prev));
            }, 3000);
          }, 150);
        };

        setCreating(false);
        if (editingFaqId === INLINE_NEW_FAQ_ID) {
          closeInlineCreate(highlightCreatedFaq);
        } else {
          highlightCreatedFaq();
        }
      }
    };

    const onFailure = (error) => {
      console.error("Failed to fetch FAQs:", error);
      message.error(`Failed to load FAQs: ${getErrorMessage(error)}`);
      if (pendingCreatedFaqRef.current) {
        pendingCreatedFaqRef.current = null;
        setCreating(false);
      }
      setFaqs([]);
    };

    dispatch(
      listFaqsApi({
        query,
        is_verified: isVerified,
        source_id: sourceId,
        tags: tagsParam,
        limit,
        offset,
        finalCallback: () => setLoading(false),
        successCallback: onSuccess,
        failureCallback: onFailure,
      })
    );
  };

  // Fetch FAQs when filters or pagination changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [searchText]);

  useEffect(() => {
    const isVerifiedParam =
      statusFilter === "all" ? undefined : statusFilter === "verified";
    fetchFaqs(
      debouncedSearchText.trim(),
      isVerifiedParam,
      sourceFilter?.id,
      tagsFilter,
      currentPage,
      pageSize
    );
  }, [
    debouncedSearchText,
    statusFilter,
    sourceFilter,
    tagsFilter,
    currentPage,
    pageSize,
    refreshKey,
  ]);

  const filteredFaqs = faqs;
  const isInlineCreateOpen =
    inlineDraftMounted &&
    editingFaqId === INLINE_NEW_FAQ_ID &&
    Boolean(editingFaqData);
  const inlineDraftFaq = isInlineCreateOpen
    ? {
      id: INLINE_NEW_FAQ_ID,
      questions: Array.isArray(editingFaqData?.questions) ? editingFaqData.questions : [],
      answer: editingFaqData?.answer || "",
      attachments: Array.isArray(editingFaqData?.attachments)
        ? editingFaqData.attachments
        : [],
      status: editingFaqData?.is_verified ? "verified" : "unverified",
      sociair_id: editingFaqData?.sociair_id || "",
      tags: Array.isArray(editingFaqData?.tags) ? editingFaqData.tags : [],
      created_at: null,
      created_by: null,
      updated_at: null,
      updated_by: null,
      verified_by: null,
      source: "inline_draft",
    }
    : null;
  const faqListData = inlineDraftFaq ? [inlineDraftFaq, ...filteredFaqs] : filteredFaqs;

  const selectedFaqSet = new Set(selectedFaqIds);
  const selectedCount = selectedFaqSet.size;
  const hasSelection = selectedCount > 0;
  const allFilteredSelected =
    filteredFaqs.length > 0 && selectedCount === filteredFaqs.length;

  const deletePreviewFaqs = faqs.filter((faq) => faqIdsToDelete.includes(faq.id));

  const startInlineEdit = (faq) => {
    if (inlineDraftTimeoutRef.current) {
      clearTimeout(inlineDraftTimeoutRef.current);
      inlineDraftTimeoutRef.current = null;
    }
    setInlineDraftMounted(false);
    setInlineDraftVisible(false);
    setEditingFaqId(faq.id);
    setEditingFaqData({
      questions: normalizeQuestionsForListEditor(faq.questions),
      answer: faq.answer,
      attachments: Array.isArray(faq.attachments) ? [...faq.attachments] : [],
      attachmentFileList: [],
      sociair_id: faq.sociair_id || "",
      tags: Array.isArray(faq.tags) ? faq.tags : [],
      is_verified: faq.status === "verified",
      verified_by: faq.verified_by || "",
    });
  };

  const closeInlineCreate = (onClosed) => {
    if (inlineDraftTimeoutRef.current) {
      clearTimeout(inlineDraftTimeoutRef.current);
    }
    setInlineDraftVisible(false);
    inlineDraftTimeoutRef.current = setTimeout(() => {
      setInlineDraftMounted(false);
      setEditingFaqId(null);
      setEditingFaqData(null);
      inlineDraftTimeoutRef.current = null;
      if (typeof onClosed === "function") {
        onClosed();
      }
    }, INLINE_DRAFT_ANIMATION_MS);
  };

  const startInlineCreate = () => {
    if (inlineDraftTimeoutRef.current) {
      clearTimeout(inlineDraftTimeoutRef.current);
      inlineDraftTimeoutRef.current = null;
    }
    if (editingFaqId === INLINE_NEW_FAQ_ID && editingFaqData) {
      setInlineDraftMounted(true);
      setInlineDraftVisible(true);
      return;
    }
    setInlineDraftMounted(true);
    setEditingFaqId(INLINE_NEW_FAQ_ID);
    setEditingFaqData({
      questions: [""],
      answer: "",
      attachments: [],
      attachmentFileList: [],
      sociair_id: "",
      tags: [],
      is_verified: false,
      verified_by: "",
    });
    setTimeout(() => {
      setInlineDraftVisible(true);
    }, 0);
  };

  const cancelInlineEdit = () => {
    if (editingFaqId === INLINE_NEW_FAQ_ID) {
      closeInlineCreate();
      return;
    }
    setEditingFaqId(null);
    setEditingFaqData(null);
  };

  const saveInlineEdit = async (faqId) => {
    if (!editingFaqData) return;
    const isInlineNewFaq = faqId === INLINE_NEW_FAQ_ID;
    const cleanedQuestions = (Array.isArray(editingFaqData.questions)
      ? editingFaqData.questions
      : []
    )
      .map((q) => q.trim())
      .filter(Boolean);
    const cleanedAnswer = editingFaqData.answer.trim();

    if (!cleanedQuestions.length) {
      message.error("Please keep at least one question.");
      return;
    }

    if (!cleanedAnswer) {
      message.error("Please enter an answer.");
      return;
    }

    const cleanedTags = Array.from(
      new Set(
        (Array.isArray(editingFaqData.tags) ? editingFaqData.tags : [])
          .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
          .filter(Boolean)
      )
    );

    if (isInlineNewFaq) {
      setCreating(true);
    } else {
      setUpdating(true);
    }
    try {
      let attachments = Array.isArray(editingFaqData.attachments)
        ? [...editingFaqData.attachments]
        : [];
      const faqItem = faqs.find((faq) => faq.id === faqId);
      const uploadSource = isInlineNewFaq ? "faq" : faqItem?.source || "faq";
      const selectedAttachmentFiles = (editingFaqData?.attachmentFileList || [])
        .map((file) => file?.originFileObj)
        .filter(Boolean);

      if (selectedAttachmentFiles.length) {
        const uploadedAttachments = await uploadAttachmentsAndGetNames(
          selectedAttachmentFiles,
          uploadSource
        );
        attachments = Array.from(new Set([...attachments, ...uploadedAttachments]));
      }

      if (isInlineNewFaq) {
        const createData = {
          faqs: [
            {
              questions: cleanedQuestions,
              answer: {
                text: cleanedAnswer,
                attachment: [],
                attachments,
              },
              sociair_id: editingFaqData.sociair_id || "",
              tags: cleanedTags,
              is_verified: Boolean(editingFaqData.is_verified),
              verified_by: editingFaqData.verified_by || "",
              source: "",
            },
          ],
        };

        dispatch(
          createFaqApi({
            data: createData,
            finalCallback: () => { },
            successCallback: (data) => {
              const createdFaqId =
                data?.item?.id ||
                data?.items?.[0]?.id ||
                data?.faqs?.[0]?.id ||
                data?.id ||
                null;
              pendingCreatedFaqRef.current = { createdFaqId };
              skipNextListLoadingRef.current = true;
              message.success("FAQ added successfully");
              setRefreshKey((k) => k + 1);
            },
            failureCallback: (error) => {
              console.error("Failed to create FAQ:", error);
              message.error(`Failed to create FAQ: ${getErrorMessage(error)}`);
              setCreating(false);
            },
          })
        );
        return;
      }

      const updateData = {
        questions: cleanedQuestions,
        answer: {
          text: cleanedAnswer,
          attachments,
        },
        sociair_id: editingFaqData.sociair_id || "",
        tags: cleanedTags,
        is_verified:
          faqItem?.status === "verified",
        verified_by: editingFaqData.verified_by || "",
      };

      dispatch(
        updateFaqApi({
          faqId,
          data: updateData,
          finalCallback: () => setUpdating(false),
          successCallback: (data) => {
            const updatedFaq = mapFaqItemToViewModel(data);
            setFaqs((prevFaqs) =>
              prevFaqs.map((faq) => (faq.id === faqId ? updatedFaq : faq))
            );
            message.success("FAQ updated successfully");
            cancelInlineEdit();
          },
          failureCallback: (error) => {
            console.error("Failed to update FAQ:", error);
            message.error(`Failed to update FAQ: ${getErrorMessage(error)}`);
          },
        })
      );
    } catch (error) {
      console.error("Failed to upload attachment:", error);
      message.error("Failed to upload attachment");
      if (isInlineNewFaq) {
        setCreating(false);
      } else {
        setUpdating(false);
      }
    }
  };

  const handleDelete = () => {
    const normalizedDeleteIds = normalizeFaqIds(faqIdsToDelete);
    if (!normalizedDeleteIds.length) {
      setDeleteModalVisible(false);
      return;
    }

    setDeleting(true);
    dispatch(
      deleteFaqApi({
        faqIds: normalizedDeleteIds,
        finalCallback: () => setDeleting(false),
        successCallback: () => {
          const deleteSet = new Set(normalizedDeleteIds);
          const deletedCount = deleteSet.size;
          setFaqs((prevFaqs) => prevFaqs.filter((faq) => !deleteSet.has(faq.id)));
          setSelectedFaqIds((prev) => prev.filter((faqId) => !deleteSet.has(faqId)));
          setTotalFaqs((prevTotal) => Math.max(prevTotal - deletedCount, 0));
          setRefreshKey((k) => k + 1);
          message.success(
            deletedCount > 1
              ? `${deletedCount} FAQs deleted successfully`
              : "FAQ deleted successfully"
          );
          setDeleteModalVisible(false);
          setFaqIdsToDelete([]);
          if (deleteSet.size === 1) {
            setHighlightedFaqId(null);
          }
        },
        failureCallback: (error) => {
          console.error("Failed to delete FAQ:", error);
          message.error(`Failed to delete FAQ: ${getErrorMessage(error)}`);
        },
      })
    );
  };

  const handleFaqFilesChanged = () => {
    setStatusFilter("all");
    setSearchText("");
    setTagsFilter([]);
    setSourceFilter(null);
    setCurrentPage(1);
    setRefreshKey((k) => k + 1);
  };

  const handleViewFaqsBySource = ({ sourceId, sourceName }) => {
    setManageFilesOpen(false);
    setSearchText("");
    setStatusFilter("all");
    setTagsFilter([]);
    setSourceFilter({ id: sourceId, name: sourceName });
    setCurrentPage(1);
    setRefreshKey((k) => k + 1);
  };

  const openDeleteModal = (faqIds) => {
    setBulkDeleteIds(faqIds);
    setDeleteModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "verified":
        return "success";
      case "unverified":
        return "warning";
      default:
        return "default";
    }
  };

  const isAiSuggestedFaq = (source) => source === "agent_suggestion";

  const formatReadableDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const updateFaqStatus = (faq, nextStatus) => {
    setStatusUpdatingId(faq.id);
    dispatch(
      updateFaqApi({
        faqId: faq.id,
        data: {
          questions: faq.questions,
          answer: {
            text: faq.answer,
            attachments: faq.attachments || [],
          },
          sociair_id: faq.sociair_id || "",
          tags: Array.isArray(faq.tags) ? faq.tags : [],
          is_verified: nextStatus === "verified",
          verified_by: faq.verified_by || "",
        },
        finalCallback: () => setStatusUpdatingId(null),
        successCallback: (data) => {
          const updatedFaq = mapFaqItemToViewModel(data);
          setFaqs((prevFaqs) =>
            prevFaqs.map((item) => (item.id === faq.id ? updatedFaq : item))
          );
          setOpenStatusPopoverId(null);
          message.success(
            nextStatus === "verified"
              ? "FAQ marked as verified"
              : "FAQ marked as unverified"
          );
        },
        failureCallback: (error) => {
          console.error("Failed to update FAQ status:", error);
          message.error(`Failed to update status: ${getErrorMessage(error)}`);
        },
      })
    );
  };

  const updateFaqTags = (faq, tags) => {
    const cleanedTags = Array.from(
      new Set(
        (Array.isArray(tags) ? tags : [])
          .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
          .filter(Boolean)
      )
    );

    setTagUpdatingId(faq.id);
    dispatch(
      updateFaqApi({
        faqId: faq.id,
        data: {
          questions: faq.questions,
          answer: {
            text: faq.answer,
            attachments: faq.attachments || [],
          },
          sociair_id: faq.sociair_id || "",
          tags: cleanedTags,
          is_verified: faq.status === "verified",
          verified_by: faq.verified_by || "",
        },
        finalCallback: () => setTagUpdatingId(null),
        successCallback: (data) => {
          const updatedFaq = mapFaqItemToViewModel(data);
          const orderedUpdatedFaq = {
            ...updatedFaq,
            tags: cleanedTags,
          };
          setFaqs((prevFaqs) =>
            prevFaqs.map((item) => (item.id === faq.id ? orderedUpdatedFaq : item))
          );
          if (editingFaqId === faq.id) {
            setEditingFaqData((prev) =>
              prev
                ? {
                  ...prev,
                  tags: cleanedTags,
                }
                : prev
            );
          }
          message.success("Category updated successfully");
        },
        failureCallback: (error) => {
          console.error("Failed to update tags:", error);
          message.error(`Failed to update tags: ${getErrorMessage(error)}`);
        },
      })
    );
  };

  const scrollFaqToTop = () => {
    if (faqSectionRef.current?.scrollIntoView) {
      faqSectionRef.current.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  return (
    <div ref={faqSectionRef} className="faq-section" style={{ fontSize: "13px" }}>
      {/* Search and Filter Header */}
      <Card
        className="faq-header"
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            borderBottom: "1px solid #f0f0f0",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Title
              level={4}
              style={{
                color: "#333",
                fontWeight: 700,
                margin: 0,
              }}
            >
              FAQ Management
            </Title>
            <Tooltip title={`${totalFaqs} FAQs found for selected filters`}>
              <Tag
                color="blue"
                style={{
                  margin: 0,
                  borderRadius: 999,
                  padding: "2px 10px",
                  fontWeight: 600,
                }}
              >
                {totalFaqs} FAQs Found
              </Tag>
            </Tooltip>
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Tooltip title="Manage FAQ documents" placement="top">
              <Button
                icon={<FolderOpenOutlined />}
                size="middle"
                onClick={() => setManageFilesOpen(true)}
              >
                Manage FAQ Documents
              </Button>
            </Tooltip>
            <Tooltip title="Add a new FAQ" placement="top">
              <Button
                type="default"
                color="primary"
                variant="solid"
                icon={<PlusIcon size={16} />}
                size="middle"
                onClick={startInlineCreate}
              >
                Add New FAQ
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
          <Col xs={24} sm={12} md={9} lg={6}>
            <Input
              placeholder="Search FAQs (questions, answers)..."
              allowClear
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
              prefix={<Search size={16} />}
              size="middle"
            />
          </Col>
          <Col xs={24} sm={12} md={9} lg={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={(value) => { setStatusFilter(value ?? "all"); setCurrentPage(1); }}
              size="middle"
              allowClear
              options={[
                { label: "All Statuses", value: "all" },
                { label: "Verified", value: "verified" },
                { label: "Unverified", value: "unverified" },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={9} lg={6}>
            <Select
              mode="multiple"
              placeholder="Filter by Category"
              style={{ width: "100%" }}
              value={tagsFilter}
              onChange={(value) => {
                setTagsFilter(Array.isArray(value) ? value : []);
                setCurrentPage(1);
              }}
              size="middle"
              allowClear
              loading={loadingFaqTagSuggestions}
              options={faqTagSuggestions.map((tag) => ({ value: tag, label: tag }))}
            />
          </Col>
        </Row>

        {sourceFilter?.id ? (
          <div style={{ marginTop: "12px" }}>
            <Text type="secondary">
              Showing the FAQs from the {sourceFilter?.name || sourceFilter?.id}.
            </Text>
            <Button
              type="link"
              size="small"
              style={{ paddingInline: 8 }}
              onClick={() => {
                setSourceFilter(null);
                setCurrentPage(1);
              }}
            >
              Clear
            </Button>
          </div>
        ) : null}
      </Card>

      {/* FAQ List */}
      <List
        itemLayout="vertical"
        dataSource={faqListData}
        loading={loading}
        rowKey="id"
        locale={{
          emptyText: "No FAQs found matching your filters",
        }}
        renderItem={(faq) => {
          const isEditingCard = editingFaqId === faq.id;
          const isInlineDraftCard = faq.id === INLINE_NEW_FAQ_ID;
          const columns = [
            {
              title: 'Question',
              key: 'question',
              width: '50%',
              onCell: () => ({
                style: { verticalAlign: 'top' },
              }),
              render: () =>
                isEditingCard && editingFaqData ? (
                  <StrictBulletListEditor
                    questions={editingFaqData.questions}
                    onChange={(nextQuestions) =>
                      setEditingFaqData((prev) =>
                        prev
                          ? {
                            ...prev,
                            questions: nextQuestions,
                          }
                          : prev
                      )
                    }
                    className="faq-question-list-editor--inline"
                  />
                ) : (
                  <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'disc' }}>
                    {faq.questions.map((q, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>
                        <Text style={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}>{q}</Text>
                      </li>
                    ))}
                  </ul>
                ),
            },
            {
              title: 'Answer',
              key: 'answer',
              width: '50%',
              onCell: () => ({
                style: { verticalAlign: 'top' },
              }),
              render: () =>
                isEditingCard && editingFaqData ? (
                  <div>
                    <DebouncedTextArea
                      value={editingFaqData.answer}
                      onChange={(val) =>
                        setEditingFaqData((prev) =>
                          prev ? { ...prev, answer: val } : prev
                        )
                      }
                      autoSize={{ minRows: 4, maxRows: 20 }}
                      placeholder="Enter the answer"
                      style={{ fontSize: '1rem' }}
                    />
                    <div style={{ marginTop: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          overflowX: "auto",
                          paddingBottom: "6px",
                        }}
                      >
                        <Upload
                          accept="image/*"
                          beforeUpload={validateImageBeforeUpload}
                          multiple
                          showUploadList={false}
                          fileList={editingFaqData.attachmentFileList}
                          onChange={({ fileList }) =>
                            setEditingFaqData((prev) =>
                              prev
                                ? {
                                  ...prev,
                                  attachmentFileList: fileList.filter((file) =>
                                    isImageFile(file?.originFileObj || file)
                                  ),
                                }
                                : prev
                            )
                          }
                        >
                          <div style={uploadCardStyle}>
                            <UploadOutlined style={{ fontSize: "20px", color: "#1677ff" }} />
                            <Text style={{ fontSize: "12px", textAlign: "center" }}>
                              Upload Image
                            </Text>
                          </div>
                        </Upload>

                        <Image.PreviewGroup>
                          {(editingFaqData.attachments || []).map((attachment, index) => {
                            const src = getAttachmentPreviewSrc(attachment);
                            const attachmentKey = `edit-existing-${index}`;
                            const imageKey = `${attachmentKey}-${src || attachment || "unknown"}`;
                            return (
                              <div
                                key={`${attachment}-${index}`}
                                style={{
                                  position: "relative",
                                  flex: "0 0 auto",
                                  display: "inline-block",
                                }}
                                onMouseEnter={() => setHoveredAttachmentKey(attachmentKey)}
                                onMouseLeave={() => setHoveredAttachmentKey(null)}
                              >
                                {src ? (
                                  <Image
                                    src={src}
                                    alt={"Attachment"}
                                    style={getFaqImagePreviewStyle(imageKey)}
                                    onError={() => markImageFailed(imageKey)}
                                    onLoad={() => clearImageFailed(imageKey)}
                                    preview
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "80px",
                                      borderRadius: "8px",
                                      border: "1px solid #f0f0f0",
                                      padding: "8px",
                                      boxSizing: "border-box",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Text style={{ fontSize: "12px" }} ellipsis>
                                      Image
                                    </Text>
                                  </div>
                                )}
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  onClick={() => removeEditingAttachmentAt(index)}
                                  style={{
                                    position: "absolute",
                                    top: "6px",
                                    right: "6px",
                                    width: "26px",
                                    height: "26px",
                                    minWidth: "26px",
                                    padding: 0,
                                    borderRadius: "999px",
                                    display:
                                      hoveredAttachmentKey === attachmentKey
                                        ? "inline-flex"
                                        : "none",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "rgba(255,255,255,0.95)",
                                    border: "1px solid #ffd6d6",
                                  }}
                                  icon={<Trash2 size={14} />}
                                  aria-label="Remove attachment"
                                />
                              </div>
                            );
                          })}

                          {(editingFaqData.attachmentFileList || []).map((file) => {
                            const src = getSelectedFilePreviewSrc(file);
                            const attachmentKey = `edit-selected-${file.uid}`;
                            const imageKey = `${attachmentKey}-${src || file.name || "unknown"}`;
                            return (
                              <div
                                key={file.uid}
                                style={{
                                  position: "relative",
                                  flex: "0 0 auto",
                                  display: "inline-block",
                                }}
                                onMouseEnter={() => setHoveredAttachmentKey(attachmentKey)}
                                onMouseLeave={() => setHoveredAttachmentKey(null)}
                              >
                                {src ? (
                                  <Image
                                    src={src}
                                    alt={file.name || "Selected image"}
                                    style={getFaqImagePreviewStyle(imageKey)}
                                    onError={() => markImageFailed(imageKey)}
                                    onLoad={() => clearImageFailed(imageKey)}
                                    preview
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "80px",
                                      borderRadius: "8px",
                                      border: "1px solid #f0f0f0",
                                      padding: "8px",
                                      boxSizing: "border-box",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Text style={{ fontSize: "12px" }} ellipsis>
                                      Image
                                    </Text>
                                  </div>
                                )}
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  onClick={() => removeEditingSelectedFileByUid(file.uid)}
                                  style={{
                                    position: "absolute",
                                    top: "6px",
                                    right: "6px",
                                    width: "26px",
                                    height: "26px",
                                    minWidth: "26px",
                                    padding: 0,
                                    borderRadius: "999px",
                                    display:
                                      hoveredAttachmentKey === attachmentKey
                                        ? "inline-flex"
                                        : "none",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "rgba(255,255,255,0.95)",
                                    border: "1px solid #ffd6d6",
                                  }}
                                  icon={<Trash2 size={14} />}
                                  aria-label="Remove attachment"
                                />
                              </div>
                            );
                          })}
                        </Image.PreviewGroup>
                      </div>
                      {!editingFaqData.attachments?.length &&
                        !editingFaqData.attachmentFileList?.length ? (
                        <Text type="secondary">No attachment</Text>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div>
                    <Text style={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}>{faq.answer}</Text>
                    {faq.attachments?.length > 0 && (
                      <div
                        style={{
                          marginTop: "8px",
                          display: "flex",
                          gap: "10px",
                          overflowX: "auto",
                          paddingBottom: "6px",
                        }}
                      >
                        <Image.PreviewGroup>
                          {faq.attachments.map((attachment, index) => (
                            <div
                              key={`${attachment}-${index}`}
                              style={{ flex: "0 0 auto", display: "inline-block" }}
                            >
                              {renderAttachment(
                                attachment,
                                `list-${faq.id}-${index}-${attachment || "unknown"}`
                              )}
                            </div>
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    )}
                  </div>
                ),
            },
          ];
          const dataSource = [{ key: 0 }];
          const cardWrapperStyle = isInlineDraftCard
            ? {
              overflow: "hidden",
              transition: `max-height ${INLINE_DRAFT_ANIMATION_MS}ms ease, opacity ${INLINE_DRAFT_ANIMATION_MS}ms ease, transform ${INLINE_DRAFT_ANIMATION_MS}ms ease, margin-bottom ${INLINE_DRAFT_ANIMATION_MS}ms ease`,
              maxHeight: inlineDraftVisible ? "1000px" : "0px",
              opacity: inlineDraftVisible ? 1 : 0,
              transform: inlineDraftVisible ? "translateY(0)" : "translateY(-10px)",
              marginBottom: inlineDraftVisible ? "16px" : "0px",
            }
            : {
              marginBottom: "16px",
            };
          return (
            <div key={faq.id} style={cardWrapperStyle}>
              <Card
                id={`faq-card-${faq.id}`}
                style={{
                  marginBottom: 0,
                  borderRadius: "8px",
                  boxShadow:
                    isInlineDraftCard
                      ? "0 0 0 2px rgba(22,119,255,0.15), 0 1px 3px rgba(0,0,0,0.08)"
                      : highlightedFaqId === faq.id
                        ? "0 0 0 2px rgba(22,119,255,0.2), 0 1px 3px rgba(0,0,0,0.1)"
                        : "0 1px 3px rgba(0,0,0,0.1)",
                  border:
                    isInlineDraftCard
                      ? "2px dashed #1677ff"
                      : highlightedFaqId === faq.id
                        ? "2px solid #1677ff"
                        : "1px solid #f0f0f0",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                }}
                bodyStyle={{ padding: "14px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    flexWrap: "wrap",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    {!isInlineDraftCard ? (
                      <Checkbox
                        className="faq-custom-checkbox"
                        checked={selectedFaqSet.has(faq.id)}
                        onChange={() => toggleFaqSelection(faq.id)}
                      />
                    ) : (
                      <Tag
                        color="blue"
                        style={{
                          margin: 0,
                          borderRadius: 999,
                          fontWeight: 700,
                          paddingInline: 10,
                          lineHeight: "24px",
                        }}
                      >
                        NEW FAQ
                      </Tag>
                    )}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "20px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        overflow: "hidden",
                      }}
                    >
                      {isInlineDraftCard ? (
                        <Tooltip title="Click to toggle verification status for this new FAQ">
                          <button
                            type="button"
                            onClick={() =>
                              setEditingFaqData((prev) =>
                                prev
                                  ? {
                                    ...prev,
                                    is_verified: !prev.is_verified,
                                  }
                                  : prev
                              )
                            }
                            style={{
                              margin: 0,
                              background: editingFaqData?.is_verified ? "#52c41a" : "#faad14",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: 1,
                              padding: "8px 14px",
                              borderRadius: "20px",
                              color: "#fff",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            {(editingFaqData?.is_verified ? "verified" : "unverified").toUpperCase()}
                          </button>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Click to change the status of this FAQ">
                          <Popover
                            trigger="click"
                            placement="bottomLeft"
                            align={{ offset: [0, 4] }}
                            getPopupContainer={() => document.body}
                            open={openStatusPopoverId === faq.id}
                            onOpenChange={(open) =>
                              setOpenStatusPopoverId(open ? faq.id : null)
                            }
                            arrow={false}
                            styles={{
                              body: { padding: "12px 14px", maxWidth: "260px" },
                            }}
                            content={
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <Text style={{ fontSize: "14px", color: "#595959" }}>
                                  Change the verification status for this FAQ.
                                </Text>
                                <Button
                                  type="primary"
                                  loading={statusUpdatingId === faq.id}
                                  style={{
                                    backgroundColor:
                                      faq.status === "verified" ? "#faad14" : "#52c41a",
                                    borderColor:
                                      faq.status === "verified" ? "#faad14" : "#52c41a",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                  }}
                                  onClick={() =>
                                    updateFaqStatus(
                                      faq,
                                      faq.status === "verified"
                                        ? "unverified"
                                        : "verified"
                                    )
                                  }
                                >
                                  {faq.status === "verified"
                                    ? "Mark as Unverified"
                                    : "Mark as Verified"}
                                </Button>
                              </div>
                            }
                          >
                            <button
                              type="button"
                              style={{
                                margin: 0,
                                background:
                                  getStatusColor(faq.status) === "success"
                                    ? "#52c41a"
                                    : "#faad14",
                                fontWeight: 500,
                                fontSize: "14px",
                                lineHeight: 1,
                                padding: "8px 14px",
                                borderRadius: "20px",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              {faq.status.toUpperCase()}
                            </button>
                          </Popover>
                        </Tooltip>
                      )}
                    </div>
                    {!isInlineDraftCard && isAiSuggestedFaq(faq.source) && (
                      <Tooltip title="This FAQ was suggested by AI based on user questions.">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1677ff",
                            textTransform: "uppercase",
                            background: "white",
                            borderRadius: "20px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            padding: "7px 12px",
                            lineHeight: 1,
                            cursor: "help",
                          }}
                        >
                          <iconify-icon
                            icon="mingcute:ai-line"
                            width="18"
                            height="18"
                          ></iconify-icon>
                          AI Suggested
                        </span>
                      </Tooltip>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginLeft: "auto",
                    }}
                  >
                    {isInlineDraftCard && creating ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Saving and refreshing...
                      </Text>
                    ) : null}
                    {!isInlineDraftCard ? (
                      <div
                        style={{
                          background: "white",
                          borderRadius: "20px",
                          padding: "4px 8px",
                          height: 36,
                          boxSizing: "border-box",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Tooltip
                          title={
                            <div style={{ minWidth: 240 }}>
                              <div>
                                <Text strong style={{ color: "#fff" }}>Created:</Text>{" "}
                                <Text style={{ color: "#fff" }}>
                                  {formatReadableDateTime(faq.created_at)}
                                </Text>
                              </div>
                              <div>
                                <Text strong style={{ color: "#fff" }}>Created by:</Text>{" "}
                                <Text style={{ color: "#fff" }}>
                                  {faq.created_by || "N/A"}
                                </Text>
                              </div>
                              <div style={{ marginTop: 6 }}>
                                <Text strong style={{ color: "#fff" }}>Updated:</Text>{" "}
                                <Text style={{ color: "#fff" }}>
                                  {formatReadableDateTime(faq.updated_at)}
                                </Text>
                              </div>
                              <div>
                                <Text strong style={{ color: "#fff" }}>Updated by:</Text>{" "}
                                <Text style={{ color: "#fff" }}>
                                  {faq.updated_by || "N/A"}
                                </Text>
                              </div>
                            </div>
                          }
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 28,
                              height: 28,
                              cursor: "default",
                            }}
                          >
                            <InfoCircleOutlined style={{ color: "#595959", fontSize: 16 }} />
                          </span>
                        </Tooltip>
                      </div>
                    ) : null}

                    <div
                      style={{
                        background: "white",
                        borderRadius: "20px",
                        padding: "4px 8px",
                        height: 36,
                        boxSizing: "border-box",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {isEditingCard ? (
                        <>
                          <Tooltip title={isInlineDraftCard ? "Create FAQ" : "Save FAQ"}>
                            <Button
                              type="text"
                              loading={isInlineDraftCard ? creating : updating}
                              onClick={() => saveInlineEdit(faq.id)}
                              style={{
                                color: "#52c41a",
                                padding: "4px 8px",
                              }}
                            >
                              {isInlineDraftCard
                                ? creating
                                  ? "Saving..."
                                  : "Create"
                                : "Save"}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <Button
                              type="text"
                              onClick={cancelInlineEdit}
                              disabled={isInlineDraftCard && creating}
                              style={{
                                padding: "4px 8px",
                              }}
                            >
                              Cancel
                            </Button>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit FAQ">
                          <Button
                            type="text"
                            icon={<Edit2 size={16} />}
                            onClick={() => startInlineEdit(faq)}
                            style={{
                              color: "#1890ff",
                              padding: "4px",
                            }}
                          />
                        </Tooltip>
                      )}
                      {!isInlineDraftCard ? (
                        <Tooltip title="Delete FAQ">
                          <Button
                            type="text"
                            danger
                            icon={<Trash2 size={16} />}
                            onClick={() => openDeleteModal([faq.id])}
                            style={{
                              padding: "4px",
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: "10px" }}>
                  <FaqTagsEditor
                    value={
                      isInlineDraftCard
                        ? Array.isArray(editingFaqData?.tags)
                          ? editingFaqData.tags
                          : []
                        : Array.isArray(faq.tags)
                          ? faq.tags
                          : []
                    }
                    editable
                    disabled={!isInlineDraftCard && tagUpdatingId === faq.id}
                    suggestedTags={faqTagSuggestions}
                    onChange={(value) => {
                      if (isInlineDraftCard) {
                        setEditingFaqData((prev) =>
                          prev
                            ? {
                              ...prev,
                              tags: Array.isArray(value) ? value : [],
                            }
                            : prev
                        );
                        return;
                      }
                      updateFaqTags(faq, value);
                    }}
                  />
                </div>

                {/* Table */}
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  size="small"
                  bordered={false}
                  style={{ marginTop: '10px' }}
                />
              </Card>
            </div>
          );
        }}
      />

      {/* Pagination */}
      {totalFaqs > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "24px", marginBottom: "16px" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalFaqs}
            showSizeChanger
            pageSizeOptions={["10", "20", "50", "100"]}
            onChange={(page, newPageSize) => {
              scrollFaqToTop();
              setCurrentPage(page);
              setPageSize(newPageSize);
            }}
            showTotal={(total) => `Total ${total} FAQs`}
          />
        </div>
      )}

      {hasSelection && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: "24px",
            transform: "translateX(-50%)",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "10px 14px",
            background: "rgba(255, 255, 255, 0.95)",
            borderRadius: "999px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            border: "1px solid #f0f0f0",
            backdropFilter: "blur(4px)",
          }}
        >
          <Checkbox
            className="faq-custom-checkbox faq-custom-checkbox--flat"
            checked={allFilteredSelected}
            indeterminate={selectedCount > 0 && selectedCount < filteredFaqs.length}
            onChange={(event) => selectAllFaqs(event.target.checked)}
            disabled={!filteredFaqs.length}
          >
            {allFilteredSelected ? "Unselect All" : "Select All"}
          </Checkbox>
          <Button
            type="default"
            onClick={() => setSelectedFaqIds([])}
          >
            Clear Selection
          </Button>
          <Tooltip title="Delete selected FAQs" placement="top">
            <Button
              type="default"
              danger
              onClick={() => openDeleteModal(selectedFaqIds)}
            >
              Delete Selected ({selectedCount})
            </Button>
          </Tooltip>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title={faqIdsToDelete.length > 1 ? "Delete FAQs" : "Delete FAQ"}
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={handleDelete}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: deleting }}
        confirmLoading={deleting}
      >
        <p>
          {faqIdsToDelete.length > 1
            ? `Are you sure you want to delete ${faqIdsToDelete.length} FAQs?`
            : "Are you sure you want to delete this FAQ?"}
        </p>
        {faqIdsToDelete.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Text strong>Question:</Text>{" "}
            <Text>
              {deletePreviewFaqs[0]?.questions?.[0] || "Selected FAQ"}
            </Text>
            {deletePreviewFaqs.length > 1 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">
                  ...and {deletePreviewFaqs.length - 1} more question
                  {deletePreviewFaqs.length - 1 > 1 ? "s" : ""}
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>

      <FaqFileManagerDialog
        open={manageFilesOpen}
        onClose={() => setManageFilesOpen(false)}
        onChanged={handleFaqFilesChanged}
        onViewFaqs={handleViewFaqsBySource}
      />
    </div>
  );
};

export default FaqComponent;
